import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { ClinicOverview } from '../common/model/dto/clinic';
import { Resource } from '../common/model/type/resource.enum';
import { RecordColumn } from '../common/model/ui/record-list.type';
import DateUtils from '../common/utils/services/date-utils';
import StringUtils from '../common/utils/services/string-utils';
import OvPillLink from '../components/UI/atoms/OvPillLink';
import OvRecordCount from '../components/UI/atoms/OvRecordCount';
import OvStatusChip from '../components/UI/atoms/OvStatusChip';
import useAuthorized from './use-authorized';

export function useClinicListColumns(
  getNavigationState: () => unknown
): RecordColumn<ClinicOverview>[] {
  const { t } = useTranslation();
  const applicationOperations = useAuthorized(Resource.Application);
  const auditActivityOperations = useAuthorized(
    Resource.ClinicAuditLogActivity
  );
  const auditActivityReadAllowed =
    auditActivityOperations.read || applicationOperations.supervise;

  return [
    {
      id: 'name',
      header: t('common.clinicFields.clinicName'),
      sortKey: 'name',
      cardRole: 'heading',
      renderCell: (clinic) => (
        <>
          {StringUtils.displayValue(clinic.name)}
          {clinic.is_demo_clinic && (
            <DemoTag>
              <OvStatusChip label={t('userList.demo')} tone="caution" />
            </DemoTag>
          )}
        </>
      ),
    },
    {
      id: 'sites',
      header: t('common.clinicFields.clinicSites'),
      sortKey: 'clinic_location_count',
      renderCell: (clinic) => clinic.clinic_location_count,
    },
    {
      id: 'providers',
      header: t('common.clinicFields.providers'),
      sortKey: 'clinic_location_provider_count',
      renderCell: (clinic) => (
        <OvRecordCount value={clinic.clinic_location_provider_count} />
      ),
    },
    {
      id: 'patients',
      header: t('common.clinicFields.patients'),
      sortKey: 'clinic_location_patient_count',
      renderCell: (clinic) => (
        <OvRecordCount value={clinic.clinic_location_patient_count} />
      ),
    },
    ...(auditActivityReadAllowed
      ? [
          {
            id: 'requests',
            header: t('common.clinicFields.requestCount'),
            sortKey: 'request_count',
            renderCell: (clinic: ClinicOverview) => (
              <OvRecordCount value={clinic.request_count} />
            ),
          },
        ]
      : []),
    {
      id: 'created-at',
      header: t('common.createdAt'),
      sortKey: 'created_at',
      renderCell: (clinic) =>
        clinic.created_at
          ? DateUtils.formatDate(
              new Date(clinic.created_at),
              'MMMM DD, YYYY, HH:mm:ss'
            )
          : '-',
    },
    {
      id: 'action',
      header: t('common.action'),
      isHeaderHidden: true,
      cardRole: 'footer',
      renderCell: (clinic, detailsPath = '') => (
        <OvPillLink
          to={detailsPath}
          state={getNavigationState()}
          aria-label={t('clinics.detailsFor', { name: clinic.name })}
        >
          {t('common.actions.seeDetails')}
        </OvPillLink>
      ),
    },
  ];
}

const DemoTag = styled.span`
  display: block;
  margin-top: 0.25rem;
`;
